import { useQuery } from "@tanstack/react-query";

import { appointmentsApi } from "@/api";
import { dateOnlyToQuery } from "@/api/api-client/apis/DateOnly";
import { TakeoverHistoryEntryResponse } from "@/api/api-client/models";
import { QueryKey } from "@/query/Query";

interface Params {
  fromDate: Date | null;
  toDate: Date | null;
  givenUpByCounselorId: string | null;
  takenOverByCounselorId: string | null;
}

/** Stable fallback while the query has no data yet, so consumers' memo dependencies keep a fixed identity. */
const NO_ITEMS: TakeoverHistoryEntryResponse[] = [];

/**
 * Loads the takeover history for the current date window and optional Bearbeiter:in filters. Only these
 * server-side filters are part of the query key; the free-text search narrows rows client-side, so it does
 * not refetch. The generated client formats the Date params as date-only query values. `data` is never
 * undefined: while loading it is a stable empty list.
 */
export function useTakeoverHistoryQuery({ fromDate, toDate, givenUpByCounselorId, takenOverByCounselorId }: Params) {
  const { data, isLoading } = useQuery({
    queryKey: [
      QueryKey.AppointmentTakeoverHistory,
      fromDate ? dateOnlyToQuery(fromDate) : null,
      toDate ? dateOnlyToQuery(toDate) : null,
      givenUpByCounselorId,
      takenOverByCounselorId,
    ],
    queryFn: () =>
      appointmentsApi.getTakeoverHistory({
        from: fromDate ?? undefined,
        till: toDate ?? undefined,
        givenUpByCounselorId: givenUpByCounselorId ?? undefined,
        takenOverByCounselorId: takenOverByCounselorId ?? undefined,
      }),
  });

  return { data: data ?? NO_ITEMS, isLoading };
}
